import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ShieldCheck } from "lucide-react";

const SPECS = [
  { item: "Automatic Boom Barriers", qty: "04 units", note: "Entry & exit gates" },
  { item: "Rubberised Speed Breakers", qty: "12 runs", note: "Internal driveways" },
  { item: "Retro-Reflective Sign Boards", qty: "38 boards", note: "Wayfinding & mandatory" },
  { item: "Reflective Road Studs", qty: "600+ pcs", note: "Basement ramp & podium" },
  { item: "Delineators & Cones", qty: "120 pcs", note: "Parking circulation" },
];

export const Showcase = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [140, -40]);

  return (
    <section id="project" ref={ref} className="relative py-24 lg:py-32 bg-[#0D1220] overflow-hidden" data-testid="project-section">
      <div className="absolute inset-0 bg-blueprint opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-amber-400 mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-amber-500 inline-block" /> Landmark Project
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-white leading-none" data-testid="project-heading">
            Navkar <span className="text-amber-500">Heights</span>
          </h2>
          <p className="mt-3 font-mono2 text-xs tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-amber-500" /> Residential Township · Full Perimeter Safety Fit-Out
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div style={{ y: y1 }} className="lg:col-span-7 relative border border-zinc-800 overflow-hidden h-[340px] md:h-[460px] group" data-testid="project-image-primary">
            <img
              src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Navkar Heights residential towers"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/85 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-amber-400">Exhibit A</p>
              <p className="font-display text-2xl font-bold uppercase text-white mt-1">The Towers</p>
            </div>
            <span className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-500" />
            <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-500" />
          </motion.div>

          <motion.div style={{ y: y2 }} className="lg:col-span-5 relative border border-zinc-800 overflow-hidden h-[340px] md:h-[460px] group lg:mt-16" data-testid="project-image-secondary">
            <img
              src="https://images.unsplash.com/photo-1624204386084-dd8c05e32226?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Navkar Heights architecture detail"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/85 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-amber-400">Exhibit B</p>
              <p className="font-display text-2xl font-bold uppercase text-white mt-1">Gate & Circulation</p>
            </div>
            <span className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-amber-500" />
            <span className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-amber-500" />
          </motion.div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 border border-zinc-800 bg-[#0B0F17] p-8"
          >
            <ShieldCheck className="w-8 h-8 text-amber-500 mb-5" />
            <p className="text-base leading-relaxed text-zinc-300" data-testid="project-description">
              A complete vehicle-movement ecosystem for Navkar Heights — from automated gate
              control at the perimeter to calibrated speed calming across internal driveways and
              high-visibility signage through the basement ramps. Designed, manufactured and
              installed by Rorix as a single accountable vendor.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 border-t border-zinc-800 pt-6">
              <div>
                <div className="font-display text-3xl font-bold text-amber-500">21</div>
                <div className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">Days To Install</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold text-amber-500">0</div>
                <div className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">Reported Incidents</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 border border-zinc-800 bg-[#0B0F17] p-8"
            data-testid="project-spec-list"
          >
            <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">Installation Breakdown</p>
            <div className="divide-y divide-zinc-800/80">
              {SPECS.map((s, i) => (
                <div key={s.item} className="py-4 flex items-center justify-between gap-4 group">
                  <div className="flex items-center gap-4">
                    <span className="font-mono2 text-xs text-amber-500/70 w-6">0{i + 1}</span>
                    <div>
                      <p className="font-display text-lg font-semibold uppercase tracking-wide text-white group-hover:text-amber-400 transition-colors duration-300">{s.item}</p>
                      <p className="text-xs text-zinc-500">{s.note}</p>
                    </div>
                  </div>
                  <span className="font-mono2 text-xs tracking-widest uppercase text-amber-400 border border-amber-500/30 bg-amber-500/10 px-3 py-1 whitespace-nowrap">
                    {s.qty}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
